import { Tooltip, Tag } from "antd";
import _ from "lodash";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAccessTokenDataPools, getSessionMetadata } from "redux/actions";

const sessionSelector = (state) => {
    const routeID = _.get(state, ["playlist", "data", "routeID"]);
    if (!routeID) return undefined;

    return _.get(state, ["sessions", routeID]);
};
const streamOffsetsSelector = (state) => state.playlist.data.offsets;
const dataPoolsSelector = (state) => state.dataPools;
const sessionMetadataSelector = (state) => state.playlist.metadata;
const videoSelector = (state) => state.playlist.data.video;

const SessionInfoTab = () => {
    const dispatch = useDispatch();
    const session = useSelector(sessionSelector);
    const streamOffsets = useSelector(streamOffsetsSelector);
    const dataPools = useSelector(dataPoolsSelector);
    const sessionMetadata = useSelector(sessionMetadataSelector);
    const video = useSelector(videoSelector);

    useEffect(() => {
        if (_.isEmpty(dataPools)) {
            dispatch(getAccessTokenDataPools());
        }
    }, [dispatch, dataPools]);

    useEffect(() => {
        if (_.isEmpty(sessionMetadata)) {
            dispatch(getSessionMetadata());
        }
    }, [dispatch, sessionMetadata]);

    const sessionStreamInfo = session?.stream_info || [];
    const sessionDataPoolID = session?.data_pool || null;
    const sessionTags = useMemo(() => _.get(session, "tags", []).sort((a, b) => a.localeCompare(b)), [session]);

    const sessionDataPool = useMemo(() => {
        if (!sessionDataPoolID) {
            return "Unknown";
        }

        const dpIndex = _.findIndex(dataPools, { id: sessionDataPoolID });
        const dataPoolName = _.get(dataPools, [dpIndex, "name"], "Unknown");
        return dataPoolName;
    }, [dataPools, sessionDataPoolID]);

    const combinedStreamInfo = useMemo(
        () =>
            sessionStreamInfo.map((stream, idx) => ({
                ...stream,
                offset: streamOffsets?.[idx],
            })),
        [sessionStreamInfo, streamOffsets],
    );

    return (
        <div className="SessionInfoTabMain">
            <Tag
                color="#108ee9"
                style={{ fontWeight: "bold", textAlign: "center" }}>
                This section is only visible to Super Admins
            </Tag>
            <div>
                <strong>Session's Data Pool:</strong> {sessionDataPool} (ID: {sessionDataPoolID})
            </div>
            <div>
                <strong>Reason for upload:</strong> {sessionMetadata?.reason_for_upload || "Unknown"}
            </div>
            <div style={{ display: "flex", flexDirection: "row", gap: "4px" }}>
                <strong>Session's Tags (full list):</strong>
                <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: "2px" }}>
                    {_.isEmpty(sessionTags)
                        ? "None"
                        : _.map(sessionTags, (tag) => (
                              <Tag
                                  key={tag}
                                  style={{ margin: "2px" }}>
                                  {tag}
                              </Tag>
                          ))}
                </div>
            </div>
            <div className="SessionInfoTabMainTitle">Available Session Streams:</div>
            <div className="SessionInfoTabMainStreamsList">
                {_.map(combinedStreamInfo, (streamInfo, idx) => {
                    const stream = _.get(video, streamInfo.stream_index, []);
                    const noStreamDataAndTrainCoupled = _.isEmpty(stream) && _.includes(sessionTags, "CAB_COUPLED");

                    return (
                        <div
                            className="SessionInfoTabStreamInfoMain"
                            key={idx}>
                            <div className="SessionInfoTabStreamInfoMainTopRow">
                                <div className="SessionInfoTabStreamInfoMainItem">
                                    <div className="SessionInfoTabStreamInfoMainItemValue">
                                        <span style={{ fontWeight: "bold" }}>{_.get(streamInfo, "type", "unknown")}</span> (
                                        {_.get(streamInfo, "label", "unknown")})
                                    </div>
                                </div>
                                <div className="SessionInfoTabStreamInfoMainRightSide">
                                    <div>
                                        Stream Index: <strong>{_.get(streamInfo, "stream_index", "unknown")}</strong>
                                    </div>
                                    {_.isEmpty(stream) ? (
                                        <Tooltip
                                            title={
                                                noStreamDataAndTrainCoupled
                                                    ? "This session has CAB_COUPLED tag so stream does not have data"
                                                    : "Stream does not have data"
                                            }
                                            placement="topLeft">
                                            <div className={`SessionInfoTabStreamInfoMainStatus`}>No Stream Data</div>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip
                                            title={_.get(streamInfo, "archived", false) ? "Stream has been archived" : "Stream is visible (NOT archived)"}
                                            placement="topLeft">
                                            <div className={`SessionInfoTabStreamInfoMainStatus ${_.get(streamInfo, "archived", false) ? "Archived" : ""}`}>
                                                {_.get(streamInfo, "archived", false) ? "Archived" : "Visible"}
                                            </div>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                            <div className="SessionInfoTabStreamInfoMainItem">
                                <div className="SessionInfoTabStreamInfoMainItemLabel">Offset:</div>
                                <div className="SessionInfoTabStreamInfoMainItemValue">{_.get(streamInfo, "offset", "unknown")}m</div>
                            </div>
                            <div className="SessionInfoTabStreamInfoMainItem">
                                <div className="SessionInfoTabStreamInfoMainItemLabel">Source Index:</div>
                                <div className="SessionInfoTabStreamInfoMainItemValue">{_.get(streamInfo, "source", "unknown")}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SessionInfoTab;
